import type { ReactNode } from "react";
import styles from "./Segmented.module.css";

export interface SegmentedOption<T extends string | number> {
  value: T;
  label: string;
  /** Spoken name, when the visible label is an abbreviation like "30". */
  title?: string;
}

export interface SegmentedProps<T extends string | number> {
  /** Names the whole group for assistive tech, e.g. "Report currency". */
  label: string;
  options: ReadonlyArray<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  variant?: "inset" | "solid";
}

/**
 * Radio semantics, button looks. `role="radiogroup"` with `aria-checked` is
 * what tells a screen reader these are N mutually-exclusive choices rather than
 * N unrelated buttons — which is the entire point of the control.
 */
export function Segmented<T extends string | number>({
  label,
  options,
  value,
  onChange,
  variant = "solid",
}: SegmentedProps<T>): ReactNode {
  return (
    <div className={`${styles.group} ${styles[variant]}`} role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            {...(option.title === undefined ? {} : { title: option.title })}
            className={`${styles.option} ${selected ? styles.selected : ""}`}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
